<script lang="ts" setup>
import { ref, reactive, onMounted, watch } from "vue";
import { formatDate } from "@/editor/CalenderHelper";
import DashboardChartsSmallButtonBar from "@/components/dashboard/Charts/SmallButtonBar.vue";
import DashboardChartsZuordnungenMenu from "@/components/dashboard/Charts/ZuordnungenMenu.vue";
import {
  getDisplay,
  getEinheitFromColumn,
  getKZColumn,
  getKZTitle,
  getKZValueDisplay,
} from "@/DataHelper";
import {
  AufgabeStatusZuordnung,
  getAlternateColorChart,
} from "../ChartTypesAndFunctions";
import {
  initChartBase,
  ChartBaseProperties,
  ChartBaseUiData,
  getKZAufgabeDefiniton,
  showDialogChart,
  ChartBaseParameters,
  refreshChartData,
} from "./ChartBaseStatic";
import { begriffBezeichnung } from "@/local";
import AUApi from "@/api";

interface KPIProps extends ChartBaseProperties {
  persoenlich?: boolean;
}

interface KPIUiData extends ChartBaseUiData {
  noData: boolean;
  showLoading: boolean;
  showLength: boolean;
  showZeitbereich?: boolean;
  alert: boolean;
  KPIs: KPIData[];
  titleTime: string;
  showTitleTime: boolean;
  infoText: string;
  showInfo: boolean;
  showZuordnung: boolean;
  pers: boolean;
  zuordnungen: AufgabeStatusZuordnung[];
  tooltipText?: string; // Formatierter Tooltip-Text
}

interface KPIParams extends ChartBaseParameters {}

interface KPIData {
  aic: number;
  text: string;
  color: string;
  titel: string;
  aicStamm?: number;
  tooltip?: string[];
}

const props = defineProps<KPIProps>();
const emit = defineEmits(["refresh", "info"]);

const { ui, params } = initChartBase<KPIUiData, KPIParams>(
  props,
  {
    noData: false,
    showLoading: true,
    showLength: true,
    tooltip: [],
    alert: false,
    KPIs: [],
    titleTime: "",
    showTitleTime: false,
    infoText: "",
    showInfo: false,
    showZuordnung: true,
    pers: false,
  },
  {}
);

const uiData = reactive<KPIUiData>(ui);

onMounted(() => {
  uiData.showTitleTime = getKZAufgabeDefiniton(uiData, props, params, "showLastRefresh");
  refresh();
});

watch(
  () => props.QvAICs,
  () => {
    refresh();
  }
);

async function refresh() {
  try {
    uiData.showLoading = true;
    const data = await refreshChartData(uiData, props, params);
    dataLoaded(data);
  } catch (error) {
    console.error(error);
    uiData.showLoading = false;
  }
}

function dataLoaded(data: QueryResponse) {
  const frame = uiData.zuordnungen?.filter((z) => z.gruppe !== "Frame");
  if (frame.length === 0 || frame.length === 1) {
    const drucke = uiData.zuordnungen?.filter(
      (z) => z.gruppe === "Druck" || z.gruppe === "Modell"
    );
    if (drucke.length === 0) {
      uiData.showZuordnung = false;
    }
  }
  try {
    uiData.KPIs = [];
    uiData.tooltip = [];
    uiData.showLength = false;
    const titleColumn = getKZColumn("title", data.columns);
    const checkKPI = getKZTitle(data.data, "count", data.columns);
    const kpiColor = getKZColumn("color", data.columns);

    const color = kpiColor?.meta?.farbe ?? "blue";
    if ((data?.data?.length ?? 0) === 0) {
      if (props.QuickViewMode) {
        uiData.noData = true;
        return;
      }
      uiData.infoText = "";
      const text = "0";
      const titel = uiData.title ?? "";
      uiData.KPIs.push({ aic: 0, text, color, titel });
      return;
    } else {
      let unit = "";
      let kpiColumn = getKZColumn("count", data.columns);
      if (!checkKPI) {
        uiData.showLength = true;
        kpiColumn = data.data.length;
      } else {
        try {
          unit = " " + getEinheitFromColumn(kpiColumn);
        } catch {
          console.log("keine einheit für KPI...");
        }
      }
      if (uiData.showLength) {
        for (let i = 0; i < data.data.length; i++) {
          const row = data.data[i];
          const text = getKZValueDisplay(row, "info", data.columns);
          uiData.tooltip.push(text);
        }
        uiData.infoText = uiData.tooltip.join("<br>");
        uiData.showInfo = true;
        // Formatiere Tooltip-Text für Anzeige
        uiData.tooltipText = uiData.tooltip.filter((t) => t).join("\n");
      }
      const aicColumn = getKZColumn("aic", data.columns);
      if (props.QuickViewMode && props.QvAICs) {
        props.QvAICs.forEach((aic) => {
          const row = data.data.find((r: any) => getDisplay(r, aicColumn));
          let titel = props.params.title ?? "";
          if (titleColumn) {
            titel = getDisplay(row, titleColumn);
          }
          const text = getDisplay(row, kpiColumn) + " " + unit;
          uiData.KPIs.push({ aic, text, color, titel });
        });
      } else {
        const row = data.data[0];
        let titel = props.params.title ?? "";
        if (titleColumn) {
          titel = getDisplay(row, titleColumn);
        }
        let text = "";
        if (uiData.showLength) {
          text = kpiColumn;
        } else {
          text = getDisplay(row, kpiColumn) + " " + unit;
        }
        const aicStamm = data.header.aicStt;
        uiData.KPIs.push({ aic: 0, text, color, titel, aicStamm });
      }
    }
  } finally {
    uiData.showLoading = false;
    uiData.titleTime = formatDate(new Date(), "HH:mm:ss");
  }
}

function getStyle(kpi: KPIData, ix: number) {
  if (props.QuickViewMode || uiData.size) {
    // backgroundColor - wird zuviel!
    // let background = getAlternateColorChart(ix);
    // if (kpi.color) {
    //   background = kpi.color;
    // }
    return {
      height: uiData.size + "px",

      overflow: "hidden",
      cursor: uiData.zusatzInfo ? "hand" : "",
      padding: "0px",
    };
  }
  return {
    background: getAlternateColorChart(ix),
    cursor: uiData.zusatzInfo ? "hand" : "",
  };
}

function logData() {
  if (uiData.showLength) {
    uiData.alert = !uiData.alert;
  }
  console.log(params.lastQueryResponse);
}
</script>

<template>
  <div v-for="(kpi, ix) in uiData.KPIs" :key="ix" max-height="150px" max-width="400px">
    <v-card min-width="300px" max-width="400px">
      <v-progress-linear
        v-if="uiData.showLoading"
        indeterminate
        color="warning"
      ></v-progress-linear>

      <v-alert
        :icon="uiData.icon"
        variant="tonal"
        :style="getStyle(kpi, ix)"
        :color="kpi.color"
        border="top"
        :border-color="kpi.color"
        elevation="2"
        prominent
        type="info"
        max-height="150px"
      >
        <v-tooltip location="bottom" :disabled="!uiData.tooltipText">
          <template v-slot:activator="{ props }">
            <div class="mt-2" v-bind="props">{{ kpi.titel }}</div>
          </template>
          <div v-if="uiData.tooltipText" style="white-space: pre-line">
            {{ uiData.tooltipText }}
          </div>
        </v-tooltip>
        <v-card-text
          @click.stop="
            showDialogChart(
              uiData,
              props,
              params.zeitbereich ?? AUApi.Current.zeitbereich,
              kpi
            )
          "
          :class="'kpiContent ' + (uiData.zusatzInfo ? 'hasClickEvent' : '')"
          v-if="!uiData.noData"
          >{{ kpi.text }}
        </v-card-text>
        <v-card-text v-else>
          {{
            begriffBezeichnung("Web_keineDaten_gefunden", "keine Daten gefunden")
          }}</v-card-text
        >
        <v-card-actions class="pa-0 ma-0">
          <v-spacer></v-spacer>
          <span
            v-if="uiData.showZeitbereich"
            style="font-size: 10px"
            class="grey--text pr-2"
            >{{ uiData.titleZeitbereich }}</span
          >
          <span v-if="uiData.showTitleTime" style="font-size: 10px" class="grey--text">{{
            uiData.titleTime
          }}</span>
          <DashboardChartsSmallButtonBar
            @refresh="refresh"
            @info="logData"
            :showDark="false"
            :tooltipMounted="uiData.titleTime"
            :showInfo="uiData.showInfo"
          />

          <DashboardChartsZuordnungenMenu
            v-if="
              uiData.zuordnungen && uiData.zuordnungen.length > 0 && uiData.showZuordnung
            "
            :zuordnungen="uiData.zuordnungen"
            :aic="kpi.aic"
            :persoenlich="props.persoenlich"
            :kpi="true"
          />
        </v-card-actions>
      </v-alert>
    </v-card>
    <v-alert
      v-if="uiData.alert"
      :value="uiData.alert"
      :color="kpi.color"
      border="top"
      type="info"
      icon="mdi-information-outline"
      transition="scale-transition"
      :height="200"
      min-width="300px"
      max-width="400px"
    >
      <template #text>
        <div v-for="(line, index) in uiData.tooltip" :key="index">
          {{ line }}
        </div>
      </template>
    </v-alert>
  </div>
</template>

<style scoped>
.kpiContent {
  padding: 0 0 0 0;
  font-size: 2em;
}

.hasClickEvent {
  cursor: pointer;
}
</style>
